import { useEffect, useMemo, useState } from "react";
import shortid from "shortid";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  Autocomplete,
  CurrencyInput,
  DataGrid,
} from "../../../../components";
import { notification } from "../../../../services";
import { value } from "../../../../helpers";
import {
  formatDate,
  formatPrecisaoFloat,
  formatPrice,
  unformatPrice,
} from "../../../../utils";
import { parcelaData } from "../add-ordem-servico";

const OrdemServicoListaPagamentos = ({
  id,
  configuracoes,
  ordemServico,
  setOrdemServico,
  listaOrdemServicoPagamentos,
  setListaOrdemServicoPagamentos,
  condicaoPagamentoList,
  gerarPagamentos,
  setGerarPagamentos,
  condicaoSelecionada,
  setCondicaoSelecionada,
  condicaoPagParcelaList,
  setCondicaoPagParcelaList,
  meioPagamentoList,
  meiosPagamentoCliente,
  ordemServicoEntity,
  meioPagamentoSelecionado,
  setMeioPagamentoSelecionado,
  listaOrdemServicoProdutos,
  setListaOrdemServicoProdutos,
  setAcrescimoPagamento,
  produto,
  setProduto,
  removerPagamentoCadastrado,
  isFechado,
  habilitarPagamentoPrazo,
  setReciboPagamentos,
}) => {
  const [inputError, setInputError] = useState(null);

  const listaMeioPagamento = useMemo(() => {
    if (!habilitarPagamentoPrazo) {
      return !configuracoes?.exibirPagamentoSemBaixa
        ? meioPagamentoList.filter(
            (meioPagamento) => meioPagamento.baixaAutomatica,
          )
        : meioPagamentoList;
    }
    return meioPagamentoList;
  }, [id, meioPagamentoList, habilitarPagamentoPrazo]);

  const colunas = [
    {
      field: "condicaoPagamentoId",
      headerName: "Condição de Pagamento",
      flex: 250,
      valueGetter: (params) =>
        value.autoComplete(condicaoPagamentoList, params.value).descricao,
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio de Pagamento",
      flex: 300,
      sortable: false,
      valueGetter: (params) =>
        value.autoComplete(meioPagamentoList, params.value).descricao,
    },
    {
      field: "valorTitulo",
      headerName: "Valor do Título",
      flex: 300,
      sortable: false,
      valueGetter: (params) =>
        params.value > 0 ? formatPrice(params.value) : "R$ 0,00",
    },
    {
      field: "dataVencimento",
      headerName: "Vencimento",
      flex: 200,
      type: "date",
      editable: !ordemServico.id,
      sortable: false,
      valueGetter: (params) => {
        return formatDate.received(params.value);
      },
    },
  ];

  useEffect(() => {
    const valorTitulo =
      parseFloat(ordemServico?.totalOrdemServico ?? 0) -
      parseFloat(ordemServico?.totalPagamentos ?? 0);
    if (valorTitulo > 0) {
      setGerarPagamentos((prevState) => ({
        ...prevState,
        valorTitulo,
      }));
    }
  }, [ordemServico?.totalOrdemServico, ordemServico?.totalPagamentos]);

  const onChangeAutocomplete = (name, value) => {
    const ordemServicoPagamentoTemp = {
      ...gerarPagamentos,
      [name]: value ? value.id : null,
    };
    if (name === "condicaoPagamentoId") {
      const acrescimo = value?.pagamentoInfo?.acrescimo;
      setCondicaoSelecionada(value);
      setAcrescimoPagamento(acrescimo);
      recalcularProdutosAcrescimoPagamento(acrescimo);
      ordemServicoPagamentoTemp.quantidadeParcelas = value?.parcelas;
    }
    setGerarPagamentos(ordemServicoPagamentoTemp);
  };

  const handleChangeInput = (event) => {
    const ordemServicoPagamentoTemp = {
      ...gerarPagamentos,
      [event.target.name]: unformatPrice(event.target.value) || null,
    };
    setGerarPagamentos(ordemServicoPagamentoTemp);
  };

  const validarGerarParcela = () => {
    if (!gerarPagamentos?.meioPagamentoId) {
      setInputError("meioPagamentoId");
      notification.erroValidacao("Meio de pagamento");
      return false;
    }
    if (!gerarPagamentos?.condicaoPagamentoId) {
      setInputError("condicaoPagamentoId");
      notification.erroValidacao("Condição de pagamento");
      return false;
    }
    if (!gerarPagamentos?.quantidadeParcelas) {
      setInputError("quantidadeParcelas");
      notification.erroValidacao("Quantidade de Parcelas");
      return false;
    }
    if (!gerarPagamentos?.valorTitulo || gerarPagamentos.valorTitulo <= 0) {
      setInputError("valorTitulo");
      notification.erroValidacao("Valor do título");
      return false;
    }
    if (
      parseFloat(ordemServico?.totalPagamentos || 0) +
        parseFloat(gerarPagamentos.valorTitulo) >
      ordemServico.totalOrdemServico
    ) {
      notification.alertaGenericos(
        "Valor inserido ultrapassa total da Ordem de serviço",
      );
      return false;
    }
    return true;
  };

  const handleGerarParcelas = () => {
    const validarParcela = validarGerarParcela();
    if (!validarParcela) return;

    const parcelasGeradas = [];
    const valorParcela =
      gerarPagamentos.valorTitulo / gerarPagamentos.quantidadeParcelas;
    const valorFormatado = Math.floor(valorParcela * 100) / 100;
    const restoDivisao =
      gerarPagamentos.valorTitulo -
      valorFormatado * gerarPagamentos.quantidadeParcelas;
    for (
      let contador = 1;
      contador <= gerarPagamentos?.quantidadeParcelas;
      contador++
    ) {
      const valorTitulo =
        contador === gerarPagamentos.quantidadeParcelas
          ? valorFormatado + parseFloat(restoDivisao.toFixed(2))
          : valorFormatado;
      const id = shortid.generate();
      parcelasGeradas.push({
        id,
        numeroTitulo:
          id && listaOrdemServicoPagamentos.length
            ? listaOrdemServicoPagamentos.length + contador
            : contador,
        condicaoPagamentoId: gerarPagamentos?.condicaoPagamentoId,
        meioPagamentoId: gerarPagamentos?.meioPagamentoId,
        dataVencimento: parcelaData(contador, condicaoSelecionada?.periodo),
        meioPagamentoSelecionado,
        valorTitulo,
      });
      if (gerarPagamentos?.quantidadeParcelas === 1 && setReciboPagamentos) {
        setReciboPagamentos((prev) => {
          const temp = {
            id,
            meioPagamento: meioPagamentoSelecionado.descricao,
            condicaoPagamento: condicaoSelecionada.descricao,
            valorPago: valorFormatado,
            dataPagamento: new Date(),
          };
          return [...prev, temp];
        });
      }
    }
    ordemServicoEntity.setChangePagamentos([
      ...listaOrdemServicoPagamentos,
      ...parcelasGeradas,
    ]);
    setListaOrdemServicoPagamentos((prevState) => [
      ...prevState,
      ...parcelasGeradas,
    ]);
    setOrdemServico(ordemServicoEntity);
    setGerarPagamentos({
      valorTitulo:
        ordemServicoEntity?.totalOrdemServico -
        parseFloat(ordemServicoEntity?.totalPagamentos ?? 0),
    });
    notification.sucessoGenericos("Parcelas geradas com sucesso");
  };

  const onSubmitParcelas = (event) => {
    event.preventDefault();
    handleGerarParcelas();
  };

  const onClickDeletePagamentos = async (idParcela) => {
    const parcelaADeletar = listaOrdemServicoPagamentos.find(
      ({ id }) => id == idParcela,
    );
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir essa parcela?`,
    );
    if (retornoAlerta.isConfirmed) {
      if (id && typeof idParcela === "number") {
        removerPagamentoCadastrado([idParcela]);
      } else {
        const data = listaOrdemServicoPagamentos.filter(
          (item) => item.id !== idParcela,
        );
        setGerarPagamentos({
          ...gerarPagamentos,
          valorTitulo:
            gerarPagamentos?.valorTitulo + parcelaADeletar?.valorTitulo,
        });
        if (setReciboPagamentos) {
          setReciboPagamentos((prev) =>
            prev.filter((item) => item.id !== idParcela),
          );
        }
        setListaOrdemServicoPagamentos(data);
        ordemServicoEntity.setChangePagamentos(data);
        setOrdemServico(ordemServicoEntity);
      }
    }
  };

  const onClickLimparParcelas = () => {
    setGerarPagamentos({
      ...gerarPagamentos,
      valorTitulo: ordemServico.totalOrdemServico,
    });
    setListaOrdemServicoPagamentos([]);
    if (setReciboPagamentos) {
      setReciboPagamentos([]);
    }
    ordemServicoEntity.setChangePagamentos(null);
    setOrdemServico(ordemServicoEntity);
  };

  const onChangeMeioPagamento = (id, value) => {
    const condicoesPagamento = value?.condicoesPagamento ?? [];
    setMeioPagamentoSelecionado(value);
    let acrescimo = null;
    let gerarPagamentosTemp = {
      [id]: value?.id ?? null,
      condicaoPagamentoId: null,
      quantidadeParcelas: null,
    };
    const primeiraCondicao = condicoesPagamento[0];
    const condicaoPagamentoUnica = condicoesPagamento.length === 1;
    if (condicaoPagamentoUnica) {
      acrescimo = primeiraCondicao?.pagamentoInfo?.acrescimo ?? null;
      gerarPagamentosTemp = {
        [id]: value?.id ?? null,
        condicaoPagamentoId: primeiraCondicao?.id ?? null,
        quantidadeParcelas: primeiraCondicao?.parcelas ?? null,
      };
    }
    setGerarPagamentos((prev) => ({
      ...prev,
      ...gerarPagamentosTemp,
    }));
    setCondicaoSelecionada(condicaoPagamentoUnica ? primeiraCondicao : null);
    setAcrescimoPagamento(acrescimo);
    recalcularProdutosAcrescimoPagamento(acrescimo);
    setCondicaoPagParcelaList(condicoesPagamento);
  };

  function recalcularProdutosAcrescimoPagamento(acrescimo) {
    const calculoValorUnitario = (acrescimo, produto) =>
      acrescimo > 0
        ? formatPrecisaoFloat(
            produto?.valorUnitario + produto?.valorUnitario * (acrescimo / 100),
          )
        : produto?.valorUnitario;
    const calculoDesconto = (valor, produto) =>
      produto?.descontoPorcentagem
        ? (valor *
            produto?.quantidade *
            parseFloat(produto?.descontoPorcentagem)) /
          100
        : produto?.desconto;
    const valorUnitario = calculoValorUnitario(acrescimo, produto);
    if (produto) {
      setProduto((prevState) => ({
        ...prevState,
        valorUnitario,
        totalItem:
          valorUnitario * prevState?.quantidade -
          (calculoDesconto(valorUnitario, prevState) || 0),
        desconto: calculoDesconto(valorUnitario, prevState),
      }));
    }
    if (listaOrdemServicoProdutos?.length) {
      const listaItens = listaOrdemServicoProdutos.map((item) => {
        const valorUnitario = calculoValorUnitario(acrescimo, item);
        return {
          ...item,
          valorUnitario,
          desconto: calculoDesconto(valorUnitario, item),
          totalItem:
            valorUnitario * item.quantidade -
            (calculoDesconto(valorUnitario, item) || 0),
        };
      });
      setListaOrdemServicoProdutos(listaItens);
      ordemServicoEntity.setChangeItens(listaItens);
      setOrdemServico({ ...ordemServicoEntity });
    }
  }

  return (
    <>
      <Grid container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="meioPagamentoId"
                name="meioPagamentoId"
                disabled={isFechado}
                options={
                  meiosPagamentoCliente?.length
                    ? meiosPagamentoCliente
                    : listaMeioPagamento
                }
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Meio de Pagamento"
                    variant="outlined"
                    margin="normal"
                    error={inputError == "meioPagamentoId" ? true : false}
                    required
                  />
                )}
                onChange={(_, newValue) =>
                  onChangeMeioPagamento("meioPagamentoId", newValue)
                }
                value={value.autoComplete(
                  meiosPagamentoCliente?.length
                    ? meiosPagamentoCliente
                    : listaMeioPagamento,
                  gerarPagamentos?.meioPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                required
                id="condicaoPagamentoId"
                name="condicaoDePagamento"
                disabled={isFechado}
                options={condicaoPagParcelaList}
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                getOptionSelected={(option, value) =>
                  option?.descricao === value?.descricao
                }
                noOptionsText="Sem opções"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Condição de Pagamento"
                    variant="outlined"
                    margin="normal"
                    error={inputError == "condicaoPagamentoId" ? true : false}
                    required
                  />
                )}
                onChange={(e, newValue) => {
                  e.persist();
                  onChangeAutocomplete("condicaoPagamentoId", newValue);
                }}
                value={value.autoComplete(
                  condicaoPagParcelaList,
                  gerarPagamentos?.condicaoPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CurrencyInput
              id="valorTitulo"
              name="valorTitulo"
              label="Valor do Título (R$)"
              inputVariant="outlined"
              required
              disabled={isFechado}
              error={inputError == "valorTitulo" ? true : false}
              value={gerarPagamentos?.valorTitulo ?? ""}
              onChange={(event) => {
                setInputError(null);
                handleChangeInput(event);
              }}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            fullWidth
            className="d-flex flex-column align-items-center"
          >
            <Button
              fullWidth
              className="mt-2 mb-3"
              size="large"
              variant="contained"
              disabled={isFechado}
              color="primary"
              onClick={onSubmitParcelas}
            >
              <i
                className="ph-fill ph-plus-circle"
                style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
              ></i>
              Gerar Parcelas
            </Button>

            {!id && (
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="primary"
                onClick={onClickLimparParcelas}
              >
                <i
                  className="ph-fill ph-trash"
                  style={{ color: "#2196F3", fontSize: 18, marginRight: 5 }}
                ></i>
                Limpar Parcelas
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid container xs={12} fullWidth className="mt-4">
          <DataGrid
            rows={listaOrdemServicoPagamentos}
            columns={colunas}
            rowCount={listaOrdemServicoPagamentos.length}
            deleteShow={!ordemServico?.id}
            sortModel={[{ field: "condicaoPagamentoId", sort: "desc" }]}
            onClickDelete={onClickDeletePagamentos}
            disableAcoes={isFechado}
            quantidadeCamposVisiveis={4}
          />
        </Grid>

        <Grid
          container
          xs={12}
          fullWidth
          className="d-flex align-items-center justify-content-end mt-3"
        >
          <span className="me-2">Saldo a pagar:</span>
          <span className="badge bg-secondary bg-gradient bg-gradient text-wrap p-2">
            {formatPrice(
              (ordemServico?.totalOrdemServico || 0) -
                (ordemServico?.totalPagamentos || 0),
            ) || "R$ 0,00"}
          </span>
        </Grid>
      </Grid>
    </>
  );
};

export default OrdemServicoListaPagamentos;
